import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  RefreshCw,
  Search,
  Download,
  X,
  Hash,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Settings2,
  ClipboardList,
  ChevronRight,
  Calendar,
  User,
  Building2,
  ShoppingCart,
  Receipt,
  Banknote,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import CalendarModal from '../components/CalendarModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { transactionService } from '../services/transactionService';
import { TransactionResponseDTO } from '../types';

// ---- Helpers ----
const safeNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return isNaN(n) ? 0 : n;
};

const getEntryIncome = (entry: any): number =>
  entry?.entryType === 'CREDITO' ? safeNumber(entry?.amount) : 0;

const getEntryExpense = (entry: any): number =>
  entry?.entryType === 'DEBITO' ? safeNumber(entry?.amount) : 0;

const sumIncome = (entries: any[]): number =>
  (entries || []).reduce((sum, e) => sum + getEntryIncome(e), 0);

const sumExpense = (entries: any[]): number =>
  (entries || []).reduce((sum, e) => sum + getEntryExpense(e), 0);

const resolveDisplayAmount = (transaction: { type: string; entries: any[] }) => {
  const income = sumIncome(transaction.entries);
  const expense = sumExpense(transaction.entries);
  if (transaction.type === 'TRANSFERENCIA') {
    return { amount: expense || income, sign: 'transfer' as const };
  }
  if (transaction.type === 'EGRESO') {
    return { amount: expense || income, sign: 'negative' as const };
  }
  return { amount: income || expense, sign: income > 0 ? 'positive' as const : expense > 0 ? 'negative' as const : 'neutral' as const };
};

// ---- Convierte SNAKE_CASE a "Texto Legible" ----
const snakeToLabel = (str: string) =>
  str
    ?.replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Sin tipo';

// ---- Mapeos ----
const TYPE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  INGRESO:       { label: 'Ingreso',           color: 'text-success',     bg: 'bg-success/10',     icon: <ArrowUpRight className="w-4 h-4" /> },
  EGRESO:        { label: 'Egreso',            color: 'text-destructive', bg: 'bg-destructive/10', icon: <ArrowDownRight className="w-4 h-4" /> },
  COBRANZA:      { label: 'Cobranza',          color: 'text-info',        bg: 'bg-info/10',        icon: <DollarSign className="w-4 h-4" /> },
  TRANSFERENCIA: { label: 'Transferencia',     color: 'text-primary',     bg: 'bg-primary/10',     icon: <ArrowLeftRight className="w-4 h-4" /> },
  AJUSTE:        { label: 'Ajuste',            color: 'text-warning',     bg: 'bg-warning/10',     icon: <Settings2 className="w-4 h-4" /> },
  CAMBIO_MONEDA: { label: 'Cambio de Moneda',  color: 'text-warning',     bg: 'bg-warning/10',     icon: <Banknote className="w-4 h-4" /> },
  DEVOLUCION:    { label: 'Devolución',        color: 'text-destructive', bg: 'bg-destructive/10', icon: <ArrowDownRight className="w-4 h-4" /> },
  PAGO:          { label: 'Pago',              color: 'text-success',     bg: 'bg-success/10',     icon: <Receipt className="w-4 h-4" /> },
  PROVISION:     { label: 'Provisión',         color: 'text-muted-foreground', bg: 'bg-muted',     icon: <FileText className="w-4 h-4" /> },
};

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  COMPLETADA: { label: 'Completada', variant: 'default' },
  PENDIENTE:  { label: 'Pendiente',  variant: 'outline' },
  CANCELADA:  { label: 'Cancelada',  variant: 'destructive' },
  REVERTIDA:  { label: 'Revertida',  variant: 'secondary' },
};

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  CAJA:                     'Caja',
  CUENTA_CORRIENTE_CLIENTE: 'Cta. Cte. Cliente',
  DEUDORES_POR_VENTAS:      'Deudores por Ventas',
  CUENTA_PROVEEDOR:         'Cuenta Proveedor',
  CUENTA_TRANSITORIA:       'Cuenta Transitoria',
  INGRESOS_VARIOS:          'Ingresos Varios',
  GASTOS_VARIOS:            'Gastos Varios',
  EXTERNO:                  'Externo',
};

const LINKED_ENTITY_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  PAYMENT_INSTALLMENT: { label: 'Cobro de Cuota',   icon: <Receipt className="w-3.5 h-3.5" /> },
  SALE:                { label: 'Venta',             icon: <ShoppingCart className="w-3.5 h-3.5" /> },
  CUSTOMER:            { label: 'Cliente',           icon: <User className="w-3.5 h-3.5" /> },
  SUPPLIER:            { label: 'Proveedor',         icon: <Building2 className="w-3.5 h-3.5" /> },
  PROJECT:             { label: 'Proyecto',          icon: <Building2 className="w-3.5 h-3.5" /> },
  EXPENSE:             { label: 'Egreso',            icon: <TrendingDown className="w-3.5 h-3.5" /> },
  TRANSFER:            { label: 'Transferencia',     icon: <ArrowLeftRight className="w-3.5 h-3.5" /> },
};

const getLinkedEntityConfig = (type: string) =>
  LINKED_ENTITY_CONFIG[type] || { label: snakeToLabel(type), icon: <FileText className="w-3.5 h-3.5" /> };

// ---- Componente principal ----
const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionResponseDTO[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<TransactionResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [sortColumn, setSortColumn] = useState<'date' | 'type' | 'amount' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [selectedTransaction, setSelectedTransaction] = useState<TransactionResponseDTO | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [calStartOpen, setCalStartOpen] = useState(false);
  const [calEndOpen, setCalEndOpen] = useState(false);

  const dateToString = (d: Date) => d.toISOString().split('T')[0];
  const stringToDate = (s: string) => s ? new Date(s + 'T00:00:00') : undefined;
  const formatDisplayDate = (s: string) =>
    s ? new Date(s + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  const [stats, setStats] = useState({ totalIncome: 0, totalExpense: 0, balance: 0, count: 0 });

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const data = await transactionService.getAuditTransactions();
      setTransactions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar transacciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (col: 'date' | 'type' | 'amount') => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('desc');
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...transactions];

    if (selectedType !== 'ALL') filtered = filtered.filter((t) => t.type === selectedType);
    if (selectedStatus !== 'ALL') filtered = filtered.filter((t) => t.status === selectedStatus);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.description?.toLowerCase().includes(term) ||
          t.id?.toString().includes(term) ||
          t.type?.toLowerCase().includes(term)
      );
    }
    if (dateFilter.startDate) {
      filtered = filtered.filter((t) => new Date(t.executedAt) >= new Date(dateFilter.startDate));
    }
    if (dateFilter.endDate) {
      filtered = filtered.filter((t) => new Date(t.executedAt) <= new Date(dateFilter.endDate + 'T23:59:59'));
    }

    if (sortColumn) {
      filtered.sort((a, b) => {
        let cmp = 0;
        if (sortColumn === 'date') cmp = new Date(a.executedAt).getTime() - new Date(b.executedAt).getTime();
        else if (sortColumn === 'type') cmp = (a.type || '').localeCompare(b.type || '');
        else if (sortColumn === 'amount') cmp = resolveDisplayAmount(a).amount - resolveDisplayAmount(b).amount;
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, selectedType, selectedStatus, searchTerm, dateFilter, sortColumn, sortDirection]);

  const calculateStats = useCallback(() => {
    let totalI = 0;
    let totalE = 0;
    filteredTransactions.forEach((t) => {
      totalI += sumIncome(t.entries);
      totalE += sumExpense(t.entries);
    });
    setStats({ totalIncome: totalI, totalExpense: totalE, balance: totalI - totalE, count: filteredTransactions.length });
  }, [filteredTransactions]);

  useEffect(() => { loadTransactions(); }, []);
  useEffect(() => { applyFilters(); }, [applyFilters]);
  useEffect(() => { calculateStats(); }, [calculateStats]);

  const formatCurrency = (amount: number, curr?: string) => {
    const symbol = curr === 'USD' ? 'US$' : '$';
    return `${symbol} ${safeNumber(amount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getTypeConfig = (type: string) =>
    TYPE_CONFIG[type?.toUpperCase()] || {
      label: snakeToLabel(type),
      color: 'text-muted-foreground',
      bg: 'bg-muted',
      icon: <FileText className="w-4 h-4" />,
    };

  const getStatusConfig = (status: string) =>
    STATUS_CONFIG[status?.toUpperCase()] || { label: snakeToLabel(status), variant: 'outline' as const };

  const exportToCSV = () => {
    const headers = ['ID', 'Fecha', 'Tipo', 'Estado', 'Descripción', 'Moneda', 'Monto'];
    const rows = filteredTransactions.map((t) => [
      t.id,
      formatDate(t.executedAt),
      getTypeConfig(t.type).label,
      t.status,
      `"${(t.description || '').replace(/"/g, '""')}"`,
      t.currency,
      (sumIncome(t.entries) - sumExpense(t.entries) !== 0
        ? sumIncome(t.entries) - sumExpense(t.entries)
        : sumIncome(t.entries) || sumExpense(t.entries)
      ).toFixed(2),
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `auditoria_transacciones_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openDetails = (t: TransactionResponseDTO) => { setSelectedTransaction(t); setDetailsOpen(true); };
  const closeDetails = () => { setDetailsOpen(false); setSelectedTransaction(null); };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('ALL');
    setSelectedStatus('ALL');
    setDateFilter({ startDate: '', endDate: '' });
  };

  const hasActiveFilters =
    searchTerm || selectedType !== 'ALL' || selectedStatus !== 'ALL' || dateFilter.startDate || dateFilter.endDate;

  // ---- Modal de detalle ----
  const detailModal = detailsOpen && selectedTransaction
    ? createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={closeDetails} />

          <div className="relative z-10 w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[88dvh] flex flex-col bg-background border border-border sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">

            {/* Header */}
            <div className="shrink-0 px-5 pt-5 pb-4 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${getTypeConfig(selectedTransaction.type).bg}`}>
                    <span className={getTypeConfig(selectedTransaction.type).color}>
                      {getTypeConfig(selectedTransaction.type).icon}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-muted-foreground">Transacción #{selectedTransaction.id}</span>
                      <Badge variant={getStatusConfig(selectedTransaction.status).variant} className="text-xs">
                        {getStatusConfig(selectedTransaction.status).label}
                      </Badge>
                    </div>
                    <h2 className="text-lg font-bold text-foreground leading-tight">
                      {getTypeConfig(selectedTransaction.type).label}
                    </h2>
                    {selectedTransaction.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{selectedTransaction.description}</p>
                    )}
                  </div>
                </div>
                <button onClick={closeDetails} className="mt-0.5 p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Metadatos en fila */}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/60 flex-wrap">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Ejecutada</p>
                  <p className="text-xs font-medium text-foreground mt-0.5">{formatDate(selectedTransaction.executedAt)}</p>
                </div>
                {selectedTransaction.accountingDate && (
                  <>
                    <div className="w-px h-6 bg-border" />
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">F. Contable</p>
                      <p className="text-xs font-medium text-foreground mt-0.5">{selectedTransaction.accountingDate}</p>
                    </div>
                  </>
                )}
                <div className="w-px h-6 bg-border" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Moneda</p>
                  <p className="text-xs font-medium text-foreground mt-0.5">{selectedTransaction.currency || '—'}</p>
                </div>
                {selectedTransaction.exchangeRate && selectedTransaction.exchangeRate !== 1 && (
                  <>
                    <div className="w-px h-6 bg-border" />
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">T. Cambio</p>
                      <p className="text-xs font-medium text-foreground mt-0.5 tabular-nums">
                        ${selectedTransaction.exchangeRate.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </>
                )}
                <div className="w-px h-6 bg-border" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Asientos</p>
                  <p className="text-xs font-medium text-foreground mt-0.5">{(selectedTransaction.entries || []).length}</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto scrollbar-custom divide-y divide-border/60">

              {/* Asientos contables */}
              <div className="px-5 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Origen — DEBITO */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <ArrowDownRight className="w-3.5 h-3.5 text-destructive shrink-0" />
                      <p className="text-[11px] font-semibold text-destructive uppercase tracking-wider">Sale de</p>
                    </div>
                    <div className="space-y-1.5">
                      {(selectedTransaction.entries || []).filter(e => e.entryType === 'DEBITO').map((entry, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-destructive/5">
                          <p className="text-xs font-medium text-foreground">
                            {ACCOUNT_TYPE_LABELS[entry.accountType?.toString()?.toUpperCase()] || snakeToLabel(entry.accountType || '')}
                          </p>
                          <p className="text-xs font-bold text-destructive tabular-nums shrink-0">
                            −{formatCurrency(safeNumber(entry.amount), entry.currency)}
                          </p>
                        </div>
                      ))}
                      {(selectedTransaction.entries || []).filter(e => e.entryType === 'DEBITO').length === 0 && (
                        <p className="text-xs text-muted-foreground italic px-1">Sin movimientos de origen</p>
                      )}
                    </div>
                  </div>

                  {/* Destino — CREDITO */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <ArrowUpRight className="w-3.5 h-3.5 text-success shrink-0" />
                      <p className="text-[11px] font-semibold text-success uppercase tracking-wider">Entra en</p>
                    </div>
                    <div className="space-y-1.5">
                      {(selectedTransaction.entries || []).filter(e => e.entryType === 'CREDITO').map((entry, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-success/5">
                          <p className="text-xs font-medium text-foreground">
                            {ACCOUNT_TYPE_LABELS[entry.accountType?.toString()?.toUpperCase()] || snakeToLabel(entry.accountType || '')}
                          </p>
                          <p className="text-xs font-bold text-success tabular-nums shrink-0">
                            +{formatCurrency(safeNumber(entry.amount), entry.currency)}
                          </p>
                        </div>
                      ))}
                      {(selectedTransaction.entries || []).filter(e => e.entryType === 'CREDITO').length === 0 && (
                        <p className="text-xs text-muted-foreground italic px-1">Sin movimientos de destino</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Entidades relacionadas */}
              {selectedTransaction.links && selectedTransaction.links.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Vinculado con</p>
                  <div className="space-y-1.5">
                    {selectedTransaction.links.map((link, i) => {
                      const cfg = getLinkedEntityConfig(link.linkedEntityType);
                      return (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-muted/40">
                          <span className="text-muted-foreground shrink-0">{cfg.icon}</span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-foreground">{cfg.label}</p>
                            {link.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{link.description}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="shrink-0 flex items-center justify-end px-5 py-3 border-t border-border">
              <Button variant="outline" size="sm" onClick={closeDetails}>Cerrar</Button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="w-full h-full p-6 space-y-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* Header */}
        <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-card to-muted/30">
          {/* Título + acciones */}
          <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <ClipboardList className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Auditoría de Transacciones</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">Historial completo de movimientos del sistema</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <ClipboardList className="w-3.5 h-3.5" />
                {transactions.length} movimiento{transactions.length !== 1 ? 's' : ''}
              </span>
              {stats.totalIncome > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-semibold">
                  <TrendingUp className="w-3.5 h-3.5" />
                  {formatCurrency(stats.totalIncome)}
                </span>
              )}
              {stats.totalExpense > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-xs font-semibold">
                  <TrendingDown className="w-3.5 h-3.5" />
                  {formatCurrency(stats.totalExpense)}
                </span>
              )}
              <Button variant="outline" size="sm" onClick={loadTransactions} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button size="sm" onClick={exportToCSV} disabled={filteredTransactions.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-col items-end gap-4 md:flex-row">
            <div className="flex flex-wrap gap-4">
              {/* Tipo */}
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">Tipo</label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos los tipos</SelectItem>
                    <SelectItem value="INGRESO">Ingreso</SelectItem>
                    <SelectItem value="EGRESO">Egreso</SelectItem>
                    <SelectItem value="TRANSFERENCIA">Transferencia</SelectItem>
                    <SelectItem value="COBRANZA">Cobranza</SelectItem>
                    <SelectItem value="AJUSTE">Ajuste</SelectItem>
                    <SelectItem value="CAMBIO_MONEDA">Cambio de Moneda</SelectItem>
                    <SelectItem value="PAGO">Pago</SelectItem>
                    <SelectItem value="DEVOLUCION">Devolución</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Estado */}
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">Estado</label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos los estados</SelectItem>
                    <SelectItem value="COMPLETADA">Completada</SelectItem>
                    <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                    <SelectItem value="CANCELADA">Cancelada</SelectItem>
                    <SelectItem value="REVERTIDA">Revertida</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Desde */}
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">Desde</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setCalStartOpen(true)}
                    className={`h-10 w-[160px] flex items-center gap-2 px-3 rounded-lg border text-sm transition-colors ${
                      dateFilter.startDate
                        ? 'border-primary/50 bg-primary/5 text-foreground'
                        : 'border-input bg-background text-muted-foreground hover:border-primary/40'
                    }`}
                  >
                    <Calendar className="w-4 h-4 shrink-0" />
                    <span className="truncate">{formatDisplayDate(dateFilter.startDate) ?? 'Seleccionar'}</span>
                  </button>
                  {dateFilter.startDate && (
                    <button
                      type="button"
                      onClick={() => setDateFilter({ ...dateFilter, startDate: '' })}
                      className="h-10 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <CalendarModal
                  open={calStartOpen}
                  onOpenChange={setCalStartOpen}
                  selectedDate={stringToDate(dateFilter.startDate)}
                  onSelectDate={(d) => setDateFilter({ ...dateFilter, startDate: dateToString(d) })}
                />
              </div>

              {/* Hasta */}
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">Hasta</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setCalEndOpen(true)}
                    className={`h-10 w-[160px] flex items-center gap-2 px-3 rounded-lg border text-sm transition-colors ${
                      dateFilter.endDate
                        ? 'border-primary/50 bg-primary/5 text-foreground'
                        : 'border-input bg-background text-muted-foreground hover:border-primary/40'
                    }`}
                  >
                    <Calendar className="w-4 h-4 shrink-0" />
                    <span className="truncate">{formatDisplayDate(dateFilter.endDate) ?? 'Seleccionar'}</span>
                  </button>
                  {dateFilter.endDate && (
                    <button
                      type="button"
                      onClick={() => setDateFilter({ ...dateFilter, endDate: '' })}
                      className="h-10 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <CalendarModal
                  open={calEndOpen}
                  onOpenChange={setCalEndOpen}
                  selectedDate={stringToDate(dateFilter.endDate)}
                  onSelectDate={(d) => setDateFilter({ ...dateFilter, endDate: dateToString(d) })}
                />
              </div>

              {/* Limpiar */}
              {hasActiveFilters && (
                <div className="space-y-2">
                  <label className="text-xs font-medium uppercase text-transparent select-none">·</label>
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="flex items-center gap-1.5 h-10 text-muted-foreground hover:text-foreground rounded-lg">
                    <X className="w-3.5 h-3.5" />
                    Limpiar
                  </Button>
                </div>
              )}
            </div>

            {/* Búsqueda */}
            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="ID, descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Cargando transacciones...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">Sin transacciones</h3>
              <p className="mb-6 text-sm text-muted-foreground max-w-xs mx-auto">
                {hasActiveFilters ? 'No hay resultados para los filtros aplicados.' : 'Aún no se registran transacciones en el sistema.'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar filtros</Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-4 py-3 text-left">
                        <span className="text-xs font-medium text-muted-foreground">#</span>
                      </th>
                      <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('date')}>
                        <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
                          Fecha {sortColumn === 'date' ? (sortDirection === 'asc' ? '↑' : '↓') : <span className="opacity-30">↕</span>}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('type')}>
                        <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
                          Tipo {sortColumn === 'type' ? (sortDirection === 'asc' ? '↑' : '↓') : <span className="opacity-30">↕</span>}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-xs font-medium text-muted-foreground">Estado</span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-xs font-medium text-muted-foreground">Descripción</span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-xs font-medium text-muted-foreground">Moneda</span>
                      </th>
                      <th className="px-4 py-3 text-right cursor-pointer select-none" onClick={() => toggleSort('amount')}>
                        <span className="text-xs font-medium text-muted-foreground inline-flex items-center justify-end gap-1">
                          Monto {sortColumn === 'amount' ? (sortDirection === 'asc' ? '↑' : '↓') : <span className="opacity-30">↕</span>}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-right">
                        <span className="text-xs font-medium text-muted-foreground">Detalle</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((transaction, idx) => {
                      const { amount: displayAmt, sign: displaySign } = resolveDisplayAmount(transaction);
                      const typeCfg = getTypeConfig(transaction.type);
                      const statusCfg = getStatusConfig(transaction.status);
                      const isLast = idx === filteredTransactions.length - 1;
                      const cell = isLast ? 'px-4 py-3' : 'px-4 py-3 border-b border-border';

                      return (
                        <tr
                          key={transaction.id}
                          className="group hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => openDetails(transaction)}
                        >
                          <td className={cell}>
                            <span className="flex items-center gap-1 text-xs font-mono text-muted-foreground">
                              <Hash className="h-3 w-3 shrink-0" />{transaction.id}
                            </span>
                          </td>
                          <td className={`${cell} whitespace-nowrap`}>
                            <span className="text-sm text-foreground">{formatDateShort(transaction.executedAt)}</span>
                          </td>
                          <td className={`${cell} whitespace-nowrap`}>
                            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg ${typeCfg.bg} ${typeCfg.color}`}>
                              {typeCfg.icon}{typeCfg.label}
                            </span>
                          </td>
                          <td className={`${cell} whitespace-nowrap`}>
                            <Badge variant={statusCfg.variant} className="text-xs">{statusCfg.label}</Badge>
                          </td>
                          <td className={cell}>
                            <span className="text-sm text-foreground max-w-[220px] truncate block">{transaction.description || '—'}</span>
                          </td>
                          <td className={`${cell} whitespace-nowrap`}>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md border border-border text-xs font-medium text-muted-foreground">
                              {transaction.currency || '—'}
                            </span>
                          </td>
                          <td className={`${cell} text-right whitespace-nowrap`}>
                            <span className={`text-sm font-semibold tabular-nums ${
                              displaySign === 'positive' ? 'text-success'
                              : displaySign === 'negative' ? 'text-destructive'
                              : displaySign === 'transfer' ? 'text-primary'
                              : 'text-muted-foreground'
                            }`}>
                              {displaySign === 'positive' ? '+' : displaySign === 'negative' ? '−' : displaySign === 'transfer' ? '⇄ ' : ''}
                              {formatCurrency(displayAmt, transaction.currency)}
                            </span>
                          </td>
                          <td className={`${cell} text-right whitespace-nowrap`}>
                            <span className="inline-flex items-center justify-end gap-1 text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors">
                              Ver detalle <ChevronRight className="w-4 h-4" />
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 mt-2">
                <p className="text-sm text-muted-foreground">
                  Mostrando <span className="font-semibold text-foreground">{filteredTransactions.length}</span>{' '}
                  de <span className="font-semibold text-foreground">{transactions.length}</span> transaccion{transactions.length !== 1 ? 'es' : ''}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {detailModal}
    </div>
  );
};

export default TransactionHistory;
